import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Event } from '@/types/event';
import { Sparkles, Download, Share2, Image as ImageIcon, Zap, Layers } from "lucide-react";
import { AIImageGenerator } from './AIImageGenerator';
import { QRCodeImageComposer } from './QRCodeImageComposer';
import { AdvancedInvitationComposer } from './ai-design/AdvancedInvitationComposer';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';

interface EventImageGeneratorProps {
  event: Event;
}

export const EventImageGenerator: React.FC<EventImageGeneratorProps> = ({ event }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string>('');
  const [finalImageWithQR, setFinalImageWithQR] = useState<string>('');
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);
  const { hasCustomBranding } = useSubscription();

  const handleImageGenerated = (imageUrl: string) => {
    console.log('AI Image generated:', imageUrl);
    setGeneratedImageUrl(imageUrl);
  };

  const handleFinalImageReady = (finalImageUrl: string) => {
    console.log('Final image with QR code ready:', finalImageUrl);
    setFinalImageWithQR(finalImageUrl);
    toast.success('Event invitation image generated with QR code!');
  };

  const downloadImage = () => {
    if (!finalImageWithQR) return;
    
    const link = document.createElement('a');
    link.href = finalImageWithQR;
    link.download = `${event.name.replace(/\s+/g, '_')}_invitation.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareImage = async () => {
    if (!finalImageWithQR) return;

    try {
      // Convert data URL to blob
      const response = await fetch(finalImageWithQR);
      const blob = await response.blob();
      
      if (navigator.share && navigator.canShare({ files: [new File([blob], 'invitation.png', { type: 'image/png' })] })) {
        await navigator.share({
          title: `${event.name} - Event Invitation`,
          text: `You're invited to ${event.name}!`,
          files: [new File([blob], 'invitation.png', { type: 'image/png' })]
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        toast.success('Image copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing image:', error);
      toast.error('Failed to share image');
    }
  };

  const resetGeneration = () => {
    setGeneratedImageUrl('');
    setFinalImageWithQR('');
    setUseAdvancedMode(false);
  };

  // Only show for Pro and Enterprise users
  if (!hasCustomBranding()) {
    return null;
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-initial">
            <Sparkles className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Generate AI Image</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Generate Event Invitation Image
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Event Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{event.name}</CardTitle>
                <CardDescription>
                  <div className="flex items-center gap-2">
                    <Badge variant={event.eventType === 'voting' ? 'default' : 'secondary'}>
                      {event.eventType === 'voting' ? 'Voting Event' : 'RSVP Event'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      by {event.hostName}
                    </span>
                  </div>
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Final Image Display */}
            {finalImageWithQR && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generated Invitation</CardTitle>
                  <CardDescription>
                    Your AI-generated invitation image with QR code and event details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border rounded-lg p-4 bg-muted/20">
                    <img 
                      src={finalImageWithQR} 
                      alt="Generated event invitation" 
                      className="w-full rounded-lg max-h-96 object-contain"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={downloadImage} className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button variant="outline" onClick={shareImage} className="flex items-center gap-2">
                      <Share2 className="w-4 h-4" />
                      Share
                    </Button>
                    <Button variant="ghost" onClick={resetGeneration}>
                      Generate Another
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Generation Mode Selection */}
            {!finalImageWithQR && !generatedImageUrl && (
              <Card>
                <CardHeader>
                  <CardTitle>Choose Generation Mode</CardTitle>
                  <CardDescription>
                    Select between quick generation or advanced AI-powered design
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={useAdvancedMode ? 'advanced' : 'quick'} onValueChange={(value) => setUseAdvancedMode(value === 'advanced')}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="quick" className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Quick Mode
                      </TabsTrigger>
                      <TabsTrigger value="advanced" className="flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Advanced AI
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="quick" className="mt-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Fast</Badge>
                          <Badge variant="outline">Simple</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Generate a beautiful invitation quickly with automatic text and QR code placement.
                        </p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="advanced" className="mt-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="default" className="bg-gradient-primary">AI Powered</Badge>
                          <Badge variant="outline">Typography Analysis</Badge>
                          <Badge variant="outline">Smart Decorations</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Advanced AI analyzes your image for optimal text placement, generates matching decorative elements, and creates professional typography.
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* AI Image Generation (only if no final image yet) */}
            {!finalImageWithQR && !useAdvancedMode && (
              <AIImageGenerator
                eventName={event.name}
                eventType={event.eventType}
                description={event.description}
                eventDate={event.eventType === 'rsvp' ? event.rsvpDate : undefined}
                eventTime={event.eventType === 'rsvp' ? event.rsvpTime : undefined}
                eventLocation={event.eventType === 'rsvp' ? event.rsvpLocation : undefined}
                hostName={event.hostName}
                deadline={event.deadline}
                onImageGenerated={handleImageGenerated}
              />
            )}

            {/* Advanced AI Invitation Composer */}
            {!finalImageWithQR && useAdvancedMode && generatedImageUrl && (
              <AdvancedInvitationComposer
                baseImageUrl={generatedImageUrl}
                qrCodeData={`https://www.eventdatepicker.com/vote/${event.shareableLink}`}
                eventData={{
                  name: event.name,
                  type: event.eventType,
                  description: event.description,
                  date: event.eventType === 'rsvp' ? event.rsvpDate : (event.options?.find(o => o.type === 'date')?.value),
                  time: event.eventType === 'rsvp' ? event.rsvpTime : (event.options?.find(o => o.type === 'time')?.value),
                  location: event.eventType === 'rsvp' ? event.rsvpLocation : (event.options?.find(o => o.type === 'location')?.value),
                  hostName: event.hostName,
                  deadline: event.deadline
                }}
                onCompositeReady={handleFinalImageReady}
              />
            )}

            {/* Standard AI Image Generation for Advanced Mode */}
            {!finalImageWithQR && useAdvancedMode && !generatedImageUrl && (
              <AIImageGenerator
                eventName={event.name}
                eventType={event.eventType}
                description={event.description}
                eventDate={event.eventType === 'rsvp' ? event.rsvpDate : undefined}
                eventTime={event.eventType === 'rsvp' ? event.rsvpTime : undefined}
                eventLocation={event.eventType === 'rsvp' ? event.rsvpLocation : undefined}
                hostName={event.hostName}
                deadline={event.deadline}
                onImageGenerated={handleImageGenerated}
              />
            )}

            {/* Hidden QR Code Composer (Quick Mode Only) */}
            {generatedImageUrl && !finalImageWithQR && !useAdvancedMode && (
              <QRCodeImageComposer
                imageUrl={generatedImageUrl}
                qrCodeData={`https://www.eventdatepicker.com/vote/${event.shareableLink}`}
                eventName={event.name}
                eventType={event.eventType}
                eventDate={event.eventType === 'rsvp' ? event.rsvpDate : (event.options?.find(o => o.type === 'date')?.value)}
                eventTime={event.eventType === 'rsvp' ? event.rsvpTime : (event.options?.find(o => o.type === 'time')?.value)}
                eventLocation={event.eventType === 'rsvp' ? event.rsvpLocation : (event.options?.find(o => o.type === 'location')?.value)}
                hostName={event.hostName}
                deadline={event.deadline}
                description={event.description}
                votingOptions={event.eventType === 'voting' ? event.options?.map(o => ({ type: o.type, value: o.value })) : undefined}
                onCompositeReady={handleFinalImageReady}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};